import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import { CustomButton } from "reusable-components/CustomButtonComponent/button";
import { getGridEntityType } from "reusable-components/GridEntityNameCell/GridEntityNameCell.utils";
import { GridSearchInput } from "reusable-components/GridSearchInput/GridSearchInput";
import { ActivityMatrixTable } from "./components/ActivityMatrixTable/ActivityMatrixTable";
import { SimpleSectionTable } from "./components/SimpleSectionTable/SimpleSectionTable";
import styles from "./PreviewImportModal.module.css";
import type { PreviewImportModalProps } from "./PreviewImportModal.types";
import {
  getActivityEntries,
  getFilteredActivityRowIndexes,
  getFilteredSimpleRows,
  getSectionEntries,
  getSectionKind,
  getSectionLabel,
  getSimpleSectionRows,
  updateActivitySectionValue,
  updateSimpleSectionValue,
} from "./PreviewImportModal.utils";

const PreviewImportModal: React.FC<PreviewImportModalProps> = ({
  show,
  loading,
  confirming,
  activityId,
  preTransferTieout,
  previewMessage,
  sourceNamesByReturnId = {},
  sourceTaxIdsByReturnId = {},
  onHide,
  onChange,
  onConfirm,
}) => {
  const entityEntries = Object.entries(preTransferTieout ?? {});
  const [activeEntityKey, setActiveEntityKey] = useState<string>("");
  const [activeSectionKey, setActiveSectionKey] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!show) return;
    if (!entityEntries.length) {
      setActiveEntityKey("");
      return;
    }
    if (!entityEntries.some(([key]) => key === activeEntityKey)) {
      setActiveEntityKey(entityEntries[0][0]);
    }
  }, [activeEntityKey, entityEntries, show]);

  const effectiveActiveEntityKey = activeEntityKey || entityEntries[0]?.[0] || "";
  const activeEntity = effectiveActiveEntityKey
    ? preTransferTieout?.[effectiveActiveEntityKey]
    : null;
  const sectionEntries = useMemo(() => getSectionEntries(activeEntity), [activeEntity]);
  const effectiveActiveSectionKey = activeSectionKey || sectionEntries[0]?.[0] || "";
  const activeSectionValue = effectiveActiveSectionKey
    ? activeEntity?.[effectiveActiveSectionKey]
    : null;
  const activeSectionKind = getSectionKind(activeSectionValue);
  const simpleRows = activeSectionKind === "simple" ? getSimpleSectionRows(activeSectionValue) : [];
  const activityEntries = activeSectionKind === "activity" ? getActivityEntries(activeSectionValue) : [];
  const filteredSimpleRows = useMemo(
    () => getFilteredSimpleRows(simpleRows, searchQuery),
    [simpleRows, searchQuery],
  );
  const filteredActivityRowIndexes = useMemo(
    () => getFilteredActivityRowIndexes(activityEntries, searchQuery),
    [activityEntries, searchQuery],
  );
  const hasRows = entityEntries.some(([, entity]) => getSectionEntries(entity).length > 0);
  const hasFilteredRows = activeSectionKind === "activity"
    ? filteredActivityRowIndexes.length > 0
    : filteredSimpleRows.length > 0;
  const hasSearchQuery = searchQuery.trim().length > 0;

  useEffect(() => {
    if (!show) return;
    if (!sectionEntries.length) {
      setActiveSectionKey("");
      return;
    }
    if (!sectionEntries.some(([key]) => key === activeSectionKey)) {
      setActiveSectionKey(sectionEntries[0][0]);
    }
  }, [activeSectionKey, sectionEntries, show]);

  const handleSimpleFieldChange = (rowIndex: number, value: string) => {
    onChange(updateSimpleSectionValue(
      preTransferTieout,
      effectiveActiveEntityKey,
      effectiveActiveSectionKey,
      rowIndex,
      value,
    ));
  };

  const handleActivityFieldChange = (activityKey: string, rowIndex: number, value: string) => {
    onChange(updateActivitySectionValue(
      preTransferTieout,
      effectiveActiveEntityKey,
      effectiveActiveSectionKey,
      activityKey,
      rowIndex,
      value,
    ));
  };

  return (
    <>
      {show && <div className={styles.modaloverlay} />}
      <Modal
        show={show}
        onHide={onHide}
        backdrop="static"
        centered
        className={styles.previewModal}
        dialogClassName={styles.previewDialog}
        contentClassName={styles.confirmModal}
      >
        <Modal.Header className={styles.modalHeader}>
          <div className={styles.headerRow}>
            <h5 className={styles.header}>Preview data before importing</h5>
            <div className={styles.headerActions}>
              <GridSearchInput
                value={searchQuery}
                placeholder="Search"
                onChange={setSearchQuery}
                width={258}
                height={34}
                disabled={loading || !hasRows}
              />
              <button type="button" className={styles.closeButton} onClick={onHide} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className={styles.modalBody}>
          {loading ? (
            <div className={styles.loadingState}>
              <ThreeCircles
                height="50"
                width="50"
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
              <p className={styles.loadingText}>Loading</p>
            </div>
          ) : !hasRows ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyText}>{previewMessage || "No preview data was returned"}</p>
            </div>
          ) : (
            <div className={styles.previewGrid}>
              {entityEntries.length > 0 && (
                <div className={styles.returnTabs} role="tablist" aria-label="Sources">
                  {entityEntries.map(([entityKey]) => (
                    (() => {
                      const SourceIcon = getGridEntityType({
                        SourceTaxId: sourceTaxIdsByReturnId[entityKey],
                      }) === "individual"
                        ? Icon.Person
                        : Icon.BuildingsFill;

                      return (
                        <button
                          key={entityKey}
                          type="button"
                          className={`${styles.returnTab} ${entityKey === effectiveActiveEntityKey ? styles.returnTabActive : ""}`}
                          onClick={() => {
                            setActiveEntityKey(entityKey);
                            setActiveSectionKey("");
                          }}
                          role="tab"
                          aria-selected={entityKey === effectiveActiveEntityKey}
                          title={entityKey}
                        >
                          <SourceIcon className={styles.returnTabIcon} aria-hidden="true" />
                          {sourceNamesByReturnId[entityKey] || entityKey}
                        </button>
                      );
                    })()
                  ))}
                </div>
              )}
              <div className={styles.previewContent}>
                <div className={styles.sectionTabs} role="tablist" aria-label="Sections">
                  {sectionEntries.map(([sectionKey]) => (
                    <button
                      key={sectionKey}
                      type="button"
                      className={`${styles.sectionTab} ${sectionKey === effectiveActiveSectionKey ? styles.sectionTabActive : ""}`}
                      onClick={() => setActiveSectionKey(sectionKey)}
                      role="tab"
                      aria-selected={sectionKey === effectiveActiveSectionKey}
                    >
                      <span>{getSectionLabel(sectionKey)}</span>
                      {sectionKey === effectiveActiveSectionKey ? (
                        <Icon.ChevronRight className={styles.sectionTabIcon} aria-hidden="true" />
                      ) : null}
                    </button>
                  ))}
                </div>
                <div className={styles.tableShell}>
                  {!hasFilteredRows ? (
                    <div className={styles.sectionEmptyState}>
                      {hasSearchQuery ? "No matching rows" : (previewMessage || "No data available")}
                    </div>
                  ) : activeSectionKind === "activity" ? (
                    <ActivityMatrixTable
                      activities={activityEntries}
                      rowIndexes={filteredActivityRowIndexes}
                      onValueChange={handleActivityFieldChange}
                    />
                  ) : (
                    <SimpleSectionTable
                      rows={filteredSimpleRows}
                      onValueChange={handleSimpleFieldChange}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <CustomButton
            variant="modalSecondary"
            type="button"
            onclick={onHide}
            content="Close"
            disable={confirming}
          />
          <CustomButton
            variant="modalPrimary"
            type="button"
            onclick={onConfirm}
            content={confirming ? "Importing" : "Import to CCH"}
            progress={confirming}
            disable={loading || confirming || !hasRows || activityId === null || activityId === ""}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PreviewImportModal;
